import CheckBoxRounded from '@mui/icons-material/CheckBoxRounded';
import NotesRounded from '@mui/icons-material/NotesRounded';
import NumbersRounded from '@mui/icons-material/NumbersRounded';
import RadioButtonCheckedRounded from '@mui/icons-material/RadioButtonCheckedRounded';
import ShortTextRounded from '@mui/icons-material/ShortTextRounded';
import ToggleOnRounded from '@mui/icons-material/ToggleOnRounded';
import type { IconComponent } from '@/modules/types';
import type {
  AttemptStatus,
  Difficulty,
  MyQuizState,
  QuestionType,
  QuizKind,
  QuizStatus,
  ShowScore,
  StudentStatus,
} from './types';

type ChipColor =
  'default' | 'primary' | 'secondary' | 'lime' | 'info' | 'success' | 'warning' | 'error';

export const QUESTION_TYPES: Record<
  QuestionType,
  { label: string; short: string; icon: IconComponent; hint: string }
> = {
  single_choice: {
    label: 'Múltipla escolha',
    short: 'Objetiva',
    icon: RadioButtonCheckedRounded,
    hint: 'Uma alternativa correta (padrão ENADE).',
  },
  multiple_choice: {
    label: 'Várias corretas',
    short: 'Várias',
    icon: CheckBoxRounded,
    hint: 'O estudante marca todas as corretas.',
  },
  true_false: {
    label: 'Verdadeiro ou falso',
    short: 'V/F',
    icon: ToggleOnRounded,
    hint: 'Duas opções: verdadeiro e falso.',
  },
  numeric: {
    label: 'Numérica',
    short: 'Numérica',
    icon: NumbersRounded,
    hint: 'Resposta é um número, com tolerância.',
  },
  short_answer: {
    label: 'Resposta curta',
    short: 'Curta',
    icon: ShortTextRounded,
    hint: 'Texto curto comparado com respostas aceitas.',
  },
  essay: {
    label: 'Discursiva',
    short: 'Discursiva',
    icon: NotesRounded,
    hint: 'Texto livre, corrigido manualmente.',
  },
};

export const DIFFICULTY: Record<Difficulty, { label: string; color: ChipColor }> = {
  easy: { label: 'Fácil', color: 'lime' },
  medium: { label: 'Média', color: 'info' },
  hard: { label: 'Difícil', color: 'warning' },
};

export const QUIZ_KINDS: Record<QuizKind, string> = {
  simulado: 'Simulado',
  prova: 'Prova',
  lista: 'Lista de exercícios',
  questionario: 'Questionário',
  diagnostica: 'Avaliação diagnóstica',
};

export const QUIZ_STATUS: Record<QuizStatus, { label: string; color: ChipColor }> = {
  draft: { label: 'Rascunho', color: 'default' },
  published: { label: 'Publicada', color: 'lime' },
  archived: { label: 'Arquivada', color: 'default' },
};

export const WINDOW_LABEL = {
  upcoming: { label: 'Em breve', color: 'info' as ChipColor },
  open: { label: 'Aberta', color: 'success' as ChipColor },
  closed: { label: 'Encerrada', color: 'default' as ChipColor },
};

export const SHOW_SCORE: Record<ShowScore, string> = {
  after_submit: 'Logo após o envio',
  after_close: 'Quando a avaliação encerrar',
  never: 'Não mostrar',
};

export const ATTEMPT_STATUS: Record<StudentStatus, { label: string; color: ChipColor }> = {
  not_started: { label: 'Não iniciou', color: 'default' },
  in_progress: { label: 'Em andamento', color: 'info' },
  needs_review: { label: 'Aguardando correção', color: 'warning' },
  graded: { label: 'Corrigida', color: 'lime' },
};

export const MY_STATE: Record<MyQuizState, { label: string; color: ChipColor }> = {
  available: { label: 'Disponível', color: 'lime' },
  in_progress: { label: 'Em andamento', color: 'info' },
  upcoming: { label: 'Em breve', color: 'default' },
  completed: { label: 'Concluída', color: 'success' },
  closed: { label: 'Encerrada', color: 'default' },
  missed: { label: 'Perdida', color: 'error' },
};

export const attemptStatusLabel = (status: AttemptStatus) => ATTEMPT_STATUS[status].label;

export const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
